import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, openFile } from "@/lib/api";
import { Upload, Eye } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { useAuth } from "@/context/AuthContext";
import { usePrefs } from "@/context/PrefsContext";
import { LANGS } from "@/i18n";
import { fmtDateTime, ACCOUNT_STATUS, DOC_STATUS, DOC_TYPES } from "@/lib/format";
import { PageHeader, StatusBadge, Empty } from "@/components/kit";

const Info = ({ k, v, testid }) => <div className="flex justify-between border-b border-border py-3 text-sm"><span className="text-muted-foreground">{k}</span><span data-testid={testid}>{v}</span></div>;

const Documents = () => {
  const [docs, setDocs] = useState([]);
  const [f, setF] = useState({ doc_type: "rg", doc_number: "", notes: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inputKey, setInputKey] = useState(0);
  const load = () => api.get("/me/documents").then((r) => setDocs(r.data));
  usePoll(() => load().catch(() => {}), 30000);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Selecione o arquivo do documento");
    if (file.size > 10 * 1024 * 1024) return toast.error("Arquivo maior que 10 MB");
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => v && fd.append(k, v));
    fd.append("file", file);
    setBusy(true);
    try { await api.post("/me/documents", fd); toast.success("Documento enviado para análise"); setF({ ...f, doc_number: "", notes: "" }); setFile(null); setInputKey((k) => k + 1); load(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="surface p-5 md:p-7" data-testid="documents-card">
      <div className="eyebrow mb-1">Documentos e verificação de identidade</div>
      <p className="text-xs text-muted-foreground mb-4">Envie a foto ou o PDF do seu documento (frente e verso no mesmo arquivo, se possível). Formatos JPG, PNG, WEBP ou PDF, até 10 MB. Seus arquivos são privados e vistos apenas pela equipe de compliance.</p>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Select value={f.doc_type} onValueChange={(v) => setF({ ...f, doc_type: v })}><SelectTrigger className="h-11" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(DOC_TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
        <Input placeholder="Número do documento" required className="h-11" value={f.doc_number} onChange={(e) => setF({ ...f, doc_number: e.target.value })} data-testid="doc-number-input" />
        <label className="md:col-span-2 flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-border px-4 py-4 text-sm hover:border-primary transition-colors">
          <Upload className="h-5 w-5 text-primary" />
          <span className="flex-1 truncate" data-testid="doc-file-name">{file ? `${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)` : "Clique para selecionar o arquivo"}</span>
          <input key={inputKey} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="sr-only" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file-input" />
        </label>
        <Input placeholder="Observação (opcional)" className="h-11" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="doc-notes-input" />
        <Button className="h-11" disabled={busy} data-testid="doc-submit-button">{busy ? "Enviando…" : "Enviar para análise"}</Button>
      </form>
      <div className="mt-5 divide-y divide-border">
        {docs.length === 0 && <Empty text="Nenhum documento enviado." />}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center justify-between py-3 text-sm" data-testid={`doc-row-${d.id}`}>
            <div><div className="font-medium">{DOC_TYPES[d.doc_type]} · <span className="num">{d.doc_number}</span></div><div className="text-xs text-muted-foreground">{fmtDateTime(d.created_at)}{d.review_note && ` · ${d.review_note}`}</div></div>
            <div className="flex items-center gap-2">
              {d.original_filename && <Button size="sm" variant="ghost" onClick={() => openFile(`/me/documents/${d.id}/file`).catch((e) => toast.error(errMsg(e)))} data-testid={`doc-view-${d.id}`}><Eye className="mr-1 h-4 w-4" />Ver</Button>}
              <StatusBadge map={DOC_STATUS} value={d.status} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CURRENCIES = [["USD", "Dólar (US$)"], ["EUR", "Euro (€)"]];

const AccountCurrency = () => {
  const { user, refreshMe } = useAuth();
  const cur = user.account_currency || "USD";
  const [busy, setBusy] = useState(false);
  const change = async (c) => {
    if (c === cur || !window.confirm(`Converter todo o saldo e o histórico da conta para ${c === "EUR" ? "Euro (€)" : "Dólar (US$)"} pela cotação atual?`)) return;
    setBusy(true);
    try { await api.patch("/me/currency", { currency: c }); await refreshMe(); toast.success("Moeda da conta alterada"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-5" data-testid="account-currency-card">
      <div className="eyebrow mb-2">Moeda da conta</div>
      <div className="flex gap-2">{CURRENCIES.map(([c, l]) => (
        <Button key={c} variant={cur === c ? "default" : "outline"} className="h-11 flex-1" disabled={busy} onClick={() => change(c)} data-testid={`account-currency-${c}`}>{l}</Button>))}</div>
      <p className="mt-2 text-xs text-muted-foreground">Saldo e extrato são exibidos na moeda escolhida. A troca converte o saldo pela cotação atual e exige não ter posições abertas, ordens pendentes ou saques em análise.</p>
    </div>
  );
};

const Summaries = () => {
  const { user, setUser } = useAuth();
  const set = async (key, field, v) => {
    try { const { data } = await api.patch("/me/summary-prefs", { [key]: v }); setUser({ ...user, ...data }); toast.success(v ? "Resumo ativado" : "Resumo desativado"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-5 space-y-3 border-t border-border pt-4 text-sm" data-testid="summary-prefs">
      <div className="eyebrow">Resumos por notificação</div>
      <div className="flex items-center justify-between gap-4"><span>Resumo diário do mercado <span className="block text-xs text-muted-foreground">Todo dia às 21h: maiores altas, quedas e seus favoritos</span></span>
        <Switch checked={user.summary_daily !== false} onCheckedChange={(v) => set("daily", "summary_daily", v)} data-testid="pref-daily-summary-switch" /></div>
      <div className="flex items-center justify-between gap-4"><span>Resumo semanal da carteira <span className="block text-xs text-muted-foreground">Toda segunda às 9h: resultado da sua carteira na semana</span></span>
        <Switch checked={user.summary_weekly !== false} onCheckedChange={(v) => set("weekly", "summary_weekly", v)} data-testid="pref-weekly-summary-switch" /></div>
    </div>
  );
};

const Preferences = () => {
  const p = usePrefs();
  return (
    <div className="surface p-5 md:p-7" data-testid="preferences-card">
      <div className="eyebrow mb-4">Acessibilidade e preferências</div>
      <div className="space-y-4 text-sm">
        <div className="flex items-center justify-between"><span>Modo escuro</span><Switch checked={p.theme === "dark"} onCheckedChange={(v) => p.setTheme(v ? "dark" : "light")} data-testid="pref-dark-switch" /></div>
        <div className="flex items-center justify-between"><span>Modo iniciante (dicas explicativas)</span><Switch checked={p.beginner} onCheckedChange={p.setBeginner} data-testid="pref-beginner-switch" /></div>
        <div className="flex items-center justify-between"><span>Botões grandes</span><Switch checked={p.largeTargets} onCheckedChange={p.setLargeTargets} data-testid="pref-large-switch" /></div>
        <div className="flex items-center justify-between gap-4"><span>Tamanho do texto</span>
          <div className="flex gap-1">{[[0.9, "A-"], [1, "A"], [1.125, "A+"], [1.25, "A++"]].map(([v, l]) => (
            <Button key={v} size="sm" variant={p.fontScale === v ? "default" : "outline"} onClick={() => p.setFontScale(v)} data-testid={`pref-font-${l}`}>{l}</Button>))}</div></div>
        <div className="flex items-center justify-between gap-4"><span>Idioma</span>
          <Select value={p.lang} onValueChange={p.setLang}><SelectTrigger className="w-48" data-testid="pref-lang-select"><SelectValue /></SelectTrigger>
            <SelectContent>{LANGS.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <Summaries />
    </div>
  );
};

export default function Profile() {
  const { user, setUser } = useAuth();
  const [phone, setPhone] = useState(user.phone || "");
  const savePhone = async () => {
    try { const { data } = await api.put("/auth/profile", { phone }); setUser({ ...user, ...data }); toast.success("Telefone atualizado"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="profile-page">
      <PageHeader eyebrow="Conta" title="Perfil e documentos" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="surface p-5 md:p-7" data-testid="profile-card">
          <div className="eyebrow mb-2">Dados da conta</div>
          <Info k="Identificador" v={<span className="num text-primary">{user.client_code}</span>} testid="profile-client-code" />
          <Info k="Nome completo" v={user.name} testid="profile-name" />
          <Info k="E-mail" v={<>{user.email} {user.email_verified ? <span className="up text-xs ml-1">verificado</span> : <span className="text-amber-500 text-xs ml-1">não verificado</span>}</>} testid="profile-email" />
          <Info k="Status" v={<StatusBadge map={ACCOUNT_STATUS} value={user.status} />} testid="profile-status" />
          <Info k="Verificação de identidade" v={<StatusBadge map={DOC_STATUS} value={user.kyc_status || "none"} />} />
          <Info k="Cliente desde" v={fmtDateTime(user.created_at)} />
          <div className="mt-4 flex gap-2"><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="h-11" data-testid="profile-phone-input" />
            <Button variant="outline" className="h-11" onClick={savePhone} data-testid="profile-phone-save">Salvar telefone</Button></div>
          <p className="mt-3 text-xs text-muted-foreground">Para alterar nome ou e-mail, contate o suporte (exige nova verificação).</p>
          <AccountCurrency />
        </div>
        <Preferences />
        <div className="lg:col-span-2"><Documents /></div>
      </div>
    </div>
  );
}
